import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button, Result } from "antd";

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("ErrorBoundary caught an error:", error, errorInfo);
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
        });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div style={{ padding: "24px", maxWidth: "600px", margin: "0 auto" }}>
                    <Result
                        status="error"
                        title="Something went wrong"
                        subTitle="An error occurred while rendering this component. Please try refreshing the page or contact support if the problem persists."
                        extra={[
                            <Button type="primary" key="retry" onClick={this.handleReset}>
                                Try again
                            </Button>,
                            <Button key="reload" onClick={() => window.location.reload()}>
                                Reload page
                            </Button>,
                        ]}
                    >
                        {process.env.NODE_ENV === "development" && this.state.error && (
                            <div
                                style={{
                                    textAlign: "left",
                                    background: "#f5f5f5",
                                    padding: "16px",
                                    borderRadius: "4px",
                                    marginTop: "16px",
                                    fontFamily: "monospace",
                                    fontSize: "12px",
                                    maxHeight: "300px",
                                    overflow: "auto",
                                }}
                            >
                                <strong>Error details (development only):</strong>
                                <pre style={{ marginTop: "8px", whiteSpace: "pre-wrap" }}>
                                    {this.state.error.toString()}
                                    {this.state.error.stack}
                                </pre>
                            </div>
                        )}
                    </Result>
                </div>
            );
        }

        return this.props.children;
    }
}

